import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Printer, Download, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtMoney = (n) => (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const iso = (d) => d.toISOString().slice(0, 10);

const TYPE_STYLES = {
  sales: "bg-emerald-500/20 text-emerald-400",
  income: "bg-sky-500/20 text-sky-400",
  purchase: "bg-orange-500/20 text-orange-400",
  expense: "bg-rose-500/20 text-rose-400",
};

export default function AccountStatementTab({ onOpenPaperworkDate, onOpenManualEntry }) {
  const today = new Date();
  const [startDate, setStartDate] = useState(iso(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [endDate, setEndDate] = useState(iso(today));
  const [typeFilter, setTypeFilter] = useState("");
  const [category, setCategory] = useState("");
  const [payMethod, setPayMethod] = useState("");
  const [stmt, setStmt] = useState(null);
  const [loading, setLoading] = useState(false);

  const buildParams = useCallback(() => {
    const p = { start_date: startDate, end_date: endDate };
    if (typeFilter) p.types = typeFilter;
    if (category.trim()) p.category = category.trim();
    if (payMethod) p.payment_method = payMethod;
    return p;
  }, [startDate, endDate, typeFilter, category, payMethod]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/account-statement`, { params: buildParams() });
      setStmt(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load statement");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { load(); }, [load]);

  const preset = (p) => {
    const now = new Date();
    if (p === "today") { setStartDate(iso(now)); setEndDate(iso(now)); }
    else if (p === "week") { const s = new Date(now); s.setDate(now.getDate() - 6); setStartDate(iso(s)); setEndDate(iso(now)); }
    else if (p === "month") { setStartDate(iso(new Date(now.getFullYear(), now.getMonth(), 1))); setEndDate(iso(now)); }
    else if (p === "year") { setStartDate(iso(new Date(now.getFullYear(), 0, 1))); setEndDate(iso(now)); }
  };

  const exportFile = (format) => {
    const p = new URLSearchParams({ ...buildParams(), format });
    window.open(`${API}/reports/account-statement/export?${p.toString()}`, "_blank");
  };

  const openRow = (r) => {
    if (r.source === "paperwork") onOpenPaperworkDate?.(r.date);
    else onOpenManualEntry?.(r.entry_id);
  };

  const rows = stmt?.rows || [];
  const selCls = "h-9 px-2 rounded-md border border-border bg-background font-mono text-xs";

  return (
    <div className="space-y-4" data-testid="statement-tab">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 print:hidden">
        <div>
          <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">From</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={selCls} data-testid="stmt-start" />
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">To</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={selCls} data-testid="stmt-end" />
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="btn-3d h-9" onClick={() => preset("today")} data-testid="stmt-preset-today">Today</Button>
          <Button size="sm" variant="outline" className="btn-3d h-9" onClick={() => preset("week")} data-testid="stmt-preset-week">Week</Button>
          <Button size="sm" variant="outline" className="btn-3d h-9" onClick={() => preset("month")} data-testid="stmt-preset-month">Month</Button>
          <Button size="sm" variant="outline" className="btn-3d h-9" onClick={() => preset("year")} data-testid="stmt-preset-year">Year</Button>
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">Type</label>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={selCls} data-testid="stmt-filter-type">
            <option value="">All types</option>
            <option value="sales">Sales</option>
            <option value="purchase">Purchases</option>
            <option value="income">Other Income</option>
            <option value="expense">Other Expenses</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">Category / vendor</label>
          <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="contains…" className="h-9 w-36 font-mono text-xs" data-testid="stmt-filter-category" />
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">Method</label>
          <select value={payMethod} onChange={(e) => setPayMethod(e.target.value)} className={selCls} data-testid="stmt-filter-method">
            <option value="">All</option>
            {["cash", "check", "ach", "card", "other"].map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
          </select>
        </div>
        <div className="ml-auto flex gap-1">
          <Button size="sm" variant="outline" className="btn-3d h-9" onClick={load} data-testid="stmt-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button size="sm" variant="outline" className="btn-3d h-9" onClick={() => exportFile("csv")} data-testid="stmt-export-csv"><Download className="w-3.5 h-3.5 mr-1" />CSV</Button>
          <Button size="sm" variant="outline" className="btn-3d h-9" onClick={() => exportFile("xlsx")} data-testid="stmt-export-xlsx"><Download className="w-3.5 h-3.5 mr-1" />Excel</Button>
          <Button size="sm" variant="outline" className="btn-3d h-9" onClick={() => window.print()} data-testid="stmt-print"><Printer className="w-3.5 h-3.5 mr-1" />Print / PDF</Button>
        </div>
      </div>

      {/* Statement table */}
      {stmt && (
        <Card className="border-border/50" data-testid="stmt-results">
          <CardContent className="p-2 overflow-x-auto">
            <table className="min-w-full text-xs font-mono tabular-nums" data-testid="stmt-table">
              <thead>
                <tr className="border-b border-border/50 text-left">
                  <th className="px-2 py-2">Date</th>
                  <th className="px-2 py-2">Type</th>
                  <th className="px-2 py-2">Category / account</th>
                  <th className="px-2 py-2">Description</th>
                  <th className="px-2 py-2">Ref #</th>
                  <th className="px-2 py-2 text-right text-rose-400">Debit</th>
                  <th className="px-2 py-2 text-right text-emerald-400">Credit</th>
                  <th className="px-2 py-2 text-right">Balance</th>
                  <th className="px-2 py-2 print:hidden" />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">No transactions in this range.</td></tr>
                ) : rows.map((r, i) => (
                  <tr key={i} className="border-b border-border/20 hover:bg-muted/30" data-testid={`stmt-row-${i}`}>
                    <td className="px-2 py-1.5 whitespace-nowrap">{r.date}</td>
                    <td className="px-2 py-1.5">
                      <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${TYPE_STYLES[r.type] || "bg-muted"}`}>{r.type}</span>
                    </td>
                    <td className="px-2 py-1.5 max-w-[160px] truncate" title={r.category}>{r.category}</td>
                    <td className="px-2 py-1.5 max-w-[200px] truncate" title={r.description}>{r.description || "—"}</td>
                    <td className="px-2 py-1.5">{r.reference || "—"}</td>
                    <td className="px-2 py-1.5 text-right text-rose-400">{r.debit ? fmtMoney(r.debit) : "—"}</td>
                    <td className="px-2 py-1.5 text-right text-emerald-400">{r.credit ? fmtMoney(r.credit) : "—"}</td>
                    <td className={`px-2 py-1.5 text-right font-bold ${r.balance >= 0 ? "text-card-foreground" : "text-rose-400"}`}>{fmtMoney(r.balance)}</td>
                    <td className="px-2 py-1.5 print:hidden">
                      <button onClick={() => openRow(r)} className="p-1 text-sky-400 hover:text-sky-300" title={r.source === "paperwork" ? "Open this date's Z-Report" : "Open in Income & Expense"} data-testid={`stmt-open-${i}`}>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-border/70 bg-muted/40 font-bold" data-testid="stmt-totals-row">
                    <td colSpan={5} className="px-2 py-2">TOTALS</td>
                    <td className="px-2 py-2 text-right text-rose-400" data-testid="stmt-total-debit">{fmtMoney(stmt.total_debit)}</td>
                    <td className="px-2 py-2 text-right text-emerald-400" data-testid="stmt-total-credit">{fmtMoney(stmt.total_credit)}</td>
                    <td className="px-2 py-2 text-right text-base" data-testid="stmt-closing-balance">{fmtMoney(stmt.closing_balance)}</td>
                    <td className="print:hidden" />
                  </tr>
                </tfoot>
              )}
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
